import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { useLangue, useTextes } from '../LangueContexte'
import { libelleVille, useVille } from '../VilleContexte'
import { Bandeau, Bloc, Bouton, Icone, Puce } from '../ui'
import { interpoler } from '../i18n'
import { derniereRecherche, ecrireCriteres, pourUneAutreVille } from '../criteres'
import { enregistrerRecherche, listerRecherches, type Ville } from '../apiRecherche'

/**
 * La feuille de choix de la ville — L13b, maquette `docs/ui/pwa/ville.html`.
 *
 * Une PAGE et non une modale, parce que la maquette en est une et parce qu'on y arrive par un
 * lien — depuis l'en-tête de la recherche, depuis la feuille de filtres, et demain depuis un
 * e-mail. Une modale n'a pas d'adresse.
 *
 * Quatre états, déclarés (règle 10 du §6 de L10), et superposables :
 *
 * 1. **« Autour de moi »** — toujours là. C'est le SEUL endroit du site où la permission de
 *    position est demandée, et elle l'est sur ce bouton, jamais au chargement (D45).
 * 2. **Ville détectée non ouverte** — la ville ouverte la plus proche, et « Prévenez-moi ».
 * 3. **La liste des villes ouvertes** avec leur nombre d'annonces. Les villes en amorçage n'y
 *    sont pas : une liste de cinquante villes vides est une liste vide. On les atteint par la
 *    saisie, et elles se disent « pas encore ouverte ».
 * 4. **Position refusée ou indisponible** — ce n'est pas une erreur, et on ne redemande pas.
 *
 * Le choix repart vers la recherche avec les filtres de la session, moins ce qui désignait un
 * lieu dans l'ancienne ville (`pourUneAutreVille`).
 */
export function FeuilleVille({ connecte }: { connecte: boolean }) {
  const t = useTextes()
  const langue = useLangue()
  const navigate = useNavigate()
  const { villes, courante, source, etatPosition, choisir, detecter, plusProcheOuverte } = useVille()
  const [requete, setRequete] = useState('')

  const aller = useCallback(
    (ville: Ville) => {
      choisir(ville.code)
      navigate(`/recherche?${ecrireCriteres(pourUneAutreVille(derniereRecherche(), ville.code))}`)
    },
    [choisir, navigate],
  )

  const autourDeMoi = useCallback(async () => {
    const ville = await detecter()

    // Une ville trouvée ET ouverte : on y va. En amorçage, on reste ici — l'état 2 dit quoi faire.
    if (ville !== null && ville.status === 'Open') {
      navigate(`/recherche?${ecrireCriteres(pourUneAutreVille(derniereRecherche(), ville.code))}`)
    }
  }, [detecter, navigate])

  const ouvertes = useMemo(() => villes.filter((ville) => ville.status === 'Open'), [villes])
  const totalAnnonces = ouvertes.reduce((somme, ville) => somme + ville.publishedListingCount, 0)

  // La saisie cherche dans les TROIS libellés : quelqu'un qui tape « Fez » sur l'interface
  // française doit trouver Fès. Les villes en amorçage n'apparaissent qu'avec une saisie.
  const filtre = requete.trim().toLocaleLowerCase()
  const correspond = (ville: Ville) =>
    filtre === '' ||
    [ville.labelFr, ville.labelAr, ville.labelEn].some((libelle) =>
      normaliser(libelle).includes(normaliser(filtre)),
    )
  const listees = (filtre === '' ? ouvertes : villes).filter(correspond)

  const enAmorcage = courante !== null && courante.status === 'Seeding'
  const procheOuverte = enAmorcage ? plusProcheOuverte(courante) : null
  const positionEnCours = etatPosition === 'en-cours'
  const positionManquee =
    etatPosition === 'refusee' || etatPosition === 'indisponible' || etatPosition === 'hors-zone'

  return (
    <section className="ville-feuille">
      <div className="page-entete">
        <h1>{t.villeChoisirTitre}</h1>
        <p>{t.villeChoisirIntro}</p>
      </div>

      <div className="pile">
        <Bloc variante="accent">
          <div className="rangee rangee--espace ville-autour">
            <div className="ville-autour__texte">
              <h2>{t.villeAutourTitre}</h2>
              <p className="secondaire">{t.villeAutourAide}</p>
            </div>
            <Bouton charge={positionEnCours} onClick={() => void autourDeMoi()}>
              <Icone nom="carte" taille="sm" />
              {t.villeAutourDeMoi}
            </Bouton>
          </div>
        </Bloc>

        {enAmorcage && courante !== null && (
          <section className="bloc recherche__amorcage">
            <div className="bloc__entete">
              <h2>{interpoler(t.villePasEncoreTitre, { marque: t.titre, ville: libelleVille(courante, langue) })}</h2>
              {(source === 'device' || source === 'ip') && <Puce ton="attention">{t.villeDetectee}</Puce>}
            </div>
            <p className="secondaire">
              {procheOuverte === null
                ? t.villePasEncoreAideSeule
                : interpoler(
                    t.villePasEncoreAide,
                    { ville: libelleVille(procheOuverte.ville, langue), km: Math.round(procheOuverte.km) },
                    langue,
                  )}
            </p>
            <div className="rangee rangee--serree ville-actions">
              <PrevenezMoi ville={courante} connecte={connecte} />
              {procheOuverte !== null && (
                <Bouton apparence="secondaire" onClick={() => aller(procheOuverte.ville)}>
                  {interpoler(t.villeChercherA, { ville: libelleVille(procheOuverte.ville, langue) })}
                </Bouton>
              )}
            </div>
            <p className="tertiaire ville-note">{t.villePrevenezMoiAide}</p>
          </section>
        )}

        <Bloc
          titre={t.villesOuvertes}
          actions={
            <span className="tertiaire">
              <bdi>{interpoler(ouvertes.length > 1 ? t.villeNVilles : t.villeNVille, { n: ouvertes.length }, langue)}</bdi>
              {' · '}
              <bdi>{interpoler(t.villeNAnnonces, { n: totalAnnonces }, langue)}</bdi>
            </span>
          }
        >
          <p className="champ ville-recherche">
            <label htmlFor="q-ville" className="visuellement-cachee">
              {t.villeChercher}
            </label>
            <input
              id="q-ville"
              type="search"
              value={requete}
              placeholder={t.villeChercherPlaceholder}
              onChange={(evenement) => setRequete(evenement.target.value)}
            />
            <span className="champ__aide">{t.villeChercherAide}</span>
          </p>

          {listees.length === 0 && <p className="secondaire">{t.villeAucune}</p>}

          <ul className="villes">
            {listees.map((ville) => {
              const estCourante = courante?.code === ville.code

              return (
                <li key={ville.code}>
                  <button
                    type="button"
                    className="ville"
                    aria-current={estCourante ? 'true' : undefined}
                    onClick={() => aller(ville)}
                  >
                    <span className={estCourante ? 'ville__icone est-courante' : 'ville__icone'}>
                      <Icone nom={estCourante ? 'coche' : 'carte'} />
                    </span>
                    <span className="ville__nom">
                      {libelleVille(ville, langue)}
                      <small>
                        {t[`region${ville.regionCode}` as keyof typeof t] ?? ville.regionCode}
                        {estCourante && ` · ${t.villeVotreVille}`}
                        {ville.status === 'Seeding' && ` · ${t.villePasEncoreOuverte}`}
                      </small>
                    </span>
                    {ville.status === 'Open' && (
                      <span className="ville__compte">
                        <bdi>
                          {interpoler(
                            ville.publishedListingCount > 1 ? t.villeNAnnonces : t.villeNAnnonce,
                            { n: ville.publishedListingCount },
                            langue,
                          )}
                        </bdi>
                      </span>
                    )}
                  </button>
                </li>
              )
            })}
          </ul>
        </Bloc>

        {positionManquee && (
          <Bandeau ton="info" icone={<Icone nom="info" />}>
            <b>{etatPosition === 'hors-zone' ? t.villeHorsZoneTitre : t.villePositionRefuseeTitre}</b>{' '}
            {etatPosition === 'hors-zone' ? t.villeHorsZoneAide : t.villePositionRefuseeAide}
          </Bandeau>
        )}
      </div>
    </section>
  )
}

/**
 * « Prévenez-moi dès la première annonce » — une recherche enregistrée `{ city }` à fréquence
 * `Instant` (D45, conception L13 §2.10). Le mécanisme d'alerte de L3 fait le reste, sans une table
 * de plus ; le back-office compte ces recherches par ville, et c'est la carte de la demande.
 *
 * Trois états : à faire, déjà fait (la recherche existe, on ne la double pas), fait à l'instant.
 * Sans compte, le bouton mène à la connexion et revient sur la recherche de cette ville, où le
 * même bouton attend.
 */
export function PrevenezMoi({ ville, connecte }: { ville: Ville; connecte: boolean }) {
  const t = useTextes()
  const langue = useLangue()
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const [etat, setEtat] = useState<'inconnu' | 'a-faire' | 'en-cours' | 'fait' | 'echec'>('inconnu')

  // Déjà demandé ? Une recherche dont la ville est le SEUL critère. Vérifié une fois, à
  // l'affichage : un second « prévenez-moi » ne préviendrait pas plus.
  useEffect(() => {
    if (!connecte) {
      setEtat('a-faire')
      return
    }

    let annule = false

    void listerRecherches()
      .then((recherches) => {
        if (annule) {
          return
        }

        const existe = recherches.some(
          (recherche) =>
            recherche.isActive &&
            recherche.criteria.cityCode === ville.code &&
            Object.entries(recherche.criteria).every(
              ([champ, valeur]) => champ === 'cityCode' || valeur === undefined || valeur === null,
            ),
        )

        setEtat(existe ? 'fait' : 'a-faire')
      })
      .catch(() => {
        if (!annule) {
          setEtat('a-faire')
        }
      })

    return () => {
      annule = true
    }
  }, [connecte, ville.code])

  const demander = useCallback(async () => {
    if (!connecte) {
      navigate(`/connexion?retour=${encodeURIComponent(pathname + search)}`)
      return
    }

    setEtat('en-cours')

    try {
      await enregistrerRecherche(libelleVille(ville, langue), { cityCode: ville.code }, 'Instant')
      setEtat('fait')
    } catch {
      setEtat('echec')
    }
  }, [connecte, navigate, pathname, search, ville, langue])

  if (etat === 'fait') {
    return (
      <p className="ville-prevenu">
        <Icone nom="coche" taille="sm" />
        <span>
          {interpoler(t.villePrevenezMoiFait, { ville: libelleVille(ville, langue) })}{' '}
          <Link to="/mes-recherches">{t.suiviRecherches}</Link>
        </span>
      </p>
    )
  }

  return (
    <>
      <Bouton charge={etat === 'en-cours'} onClick={() => void demander()}>
        <Icone nom="cloche" taille="sm" />
        {t.villePrevenezMoi}
      </Bouton>
      {etat === 'echec' && <span className="erreur">{t.villePrevenezMoiEchec}</span>}
    </>
  )
}

/** Sans accents ni casse : « fes » trouve « Fès ». */
function normaliser(texte: string): string {
  return texte
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLocaleLowerCase()
}
